/**
 * Detailed View - Full leaderboard with descriptions
 */

import { useState, useEffect } from 'react';
import { fetchLeaderboardStats, fetchChallengeRankings } from '../lib/api-client';
import { ChallengeRankCard } from '../components/ChallengeRankCard';
import type { LeaderboardStats, ChallengeRanking } from '../types';

export function DetailedView() {
  const [stats, setStats] = useState<LeaderboardStats | null>(null);
  const [rankings, setRankings] = useState<ChallengeRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();

    // Auto-refresh every 5 minutes (300000ms)
    const interval = setInterval(() => {
      loadData();
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, rankingsResponse] = await Promise.all([
        fetchLeaderboardStats(),
        fetchChallengeRankings(),
      ]);

      if (statsResponse.success && rankingsResponse.success) {
        setStats(statsResponse.data);
        setRankings(rankingsResponse.data.rankings);
      } else {
        throw new Error('Failed to load leaderboard data');
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load leaderboard data'
      );
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="app-container">
        <div className="matrix-bg"></div>
        <div className="scanlines"></div>
        <div className="glow-orb"></div>

        {/* Corner brackets */}
        <div className="corner-bracket corner-tl"></div>
        <div className="corner-bracket corner-tr"></div>
        <div className="corner-bracket corner-bl"></div>
        <div className="corner-bracket corner-br"></div>

        <div className="loading">
          <div className="loading-icon-lg">📊</div>
          <div className="loading-title">Open Questions</div>
          <div className="loading-text">
            Loading challenge data...
          </div>
          <div className="loading-subtext">
            Aggregating results from presentations
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="matrix-bg"></div>
        <div className="scanlines"></div>
        <div className="glow-orb"></div>

        {/* Corner brackets */}
        <div className="corner-bracket corner-tl"></div>
        <div className="corner-bracket corner-tr"></div>
        <div className="corner-bracket corner-bl"></div>
        <div className="corner-bracket corner-br"></div>

        <div className="error">
          <div className="error-box">
            <div className="error-icon">⚠️</div>
            <div className="error-text">{error}</div>
            <button
              onClick={loadData}
              className="retry-button"
            >
              RETRY
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Visual effects */}
      <div className="matrix-bg"></div>
      <div className="scanlines"></div>
      <div className="glow-orb"></div>

      {/* Corner brackets */}
      <div className="corner-bracket corner-tl"></div>
      <div className="corner-bracket corner-tr"></div>
      <div className="corner-bracket corner-bl"></div>
      <div className="corner-bracket corner-br"></div>

      {/* Header */}
      <header className="header">
        <h1 className="header-title">
          Funding the Commons
        </h1>
        <p className="header-subtitle">
          Open questions and challenges in public goods funding
        </p>

        {stats && (
          <>
            <div className="stats-container">
              <div className="stat-box">
                <div className="stat-number">{stats.totalQuestions}</div>
                <div className="stat-label">Open Questions</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{stats.totalChallenges}</div>
                <div className="stat-label">Challenges</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{stats.totalPresentations}</div>
                <div className="stat-label">Presentations</div>
              </div>
            </div>

            <div className="terminal-text">
              Last updated: {new Date(stats.lastUpdated).toLocaleString()}
            </div>
          </>
        )}
      </header>

      {/* Main Content */}
      <main className="main-content">
        <h2 className="section-title">CHALLENGE LEADERBOARD</h2>

        {rankings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <div className="empty-title">No Challenges Yet</div>
            <div className="empty-text">
              Submit a presentation transcript to extract open questions...
            </div>
          </div>
        ) : (
          <div>
            {rankings.map((challenge, index) => (
              <ChallengeRankCard key={challenge.challengeId} challenge={challenge} rank={index + 1} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
